import styled from 'styled-components';
import { MOBILE_QUERY } from './DraftBoard.styles';

/* Phones: the two teams stack in one scrolling column instead of sitting side by side. */
const MOBILE = `@media only screen and ${MOBILE_QUERY}`;

export const TradeWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 92%;
  max-width: 860px;
  max-height: 88vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  outline: none;
  display: flex;
  flex-direction: column;
`;

export const TradeHeader = styled.div`
  background: linear-gradient(180deg, #012d4e 0%, #004c7d 100%);
  padding: clamp(18px, 1.12vw, 43px) clamp(24px, 1.5vw, 58px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

export const TradeTitle = styled.h2`
  color: #fff;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(22px, 1.38vw, 53px);
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin: 0;
`;

export const TradeCloseBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  opacity: 0.85;
  &:hover { opacity: 1; }
`;

/* The trade as the side panel's Trade tab: fills the space under the tabs. The two team columns
   scroll on their own and the footer stays at the bottom. */
export const TradePanelBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
`;

/* Solid alert card with a darker strip on the left, inset to line up with the columns below.
   Red when the offer is turned down, green (the clock banner's) when it's accepted. */
export const StatusBar = styled.div`
  margin: clamp(16px, 1vw, 38px) clamp(24px, 1.5vw, 58px) 0;
  padding: clamp(10px, 0.62vw, 24px) clamp(18px, 1.12vw, 43px);
  border-radius: 10px;
  border-left: clamp(6px, 0.38vw, 14px) solid ${({ $accepted }) => $accepted ? '#2b6b3b' : '#8f2a20'};
  background: ${({ $accepted }) => $accepted ? '#3f8f54' : '#d64030'};
  color: #fff;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 400;
  font-size: clamp(16px, 1vw, 38px);
  flex-shrink: 0;

  strong {
    font-weight: 800;
  }

  ${MOBILE} {
    margin: 12px 16px 0;
  }
`;

export const TradeColumns = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: clamp(300px, 19vw, 720px);

  /* Out of trade attempts: both columns disabled as one block */
  ${({ $disabled }) => $disabled && `
    cursor: not-allowed;
    pointer-events: none;
    opacity: .75;
  `}

  /* One column: the whole list scrolls here rather than each team on its own. */
  ${MOBILE} {
    flex-direction: column;
    overflow-y: auto;
    min-height: 0;
  }
`;

export const TradeCol = styled.div`
  flex: 1;
  padding: clamp(20px, 1.25vw, 48px) clamp(24px, 1.5vw, 58px);
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(14px, 0.88vw, 34px);

  ${MOBILE} {
    flex: none;
    overflow: visible;
    padding: 16px;
  }
`;

export const TradeDivider = styled.div`
  width: 1px;
  background: #e5e7eb;
  flex-shrink: 0;
  align-self: stretch;

  /* Between the stacked teams: a ↕ instead of the vertical line. */
  ${MOBILE} {
    width: auto;
    height: 24px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%239ca3af'%3E%3Cpath d='M12 3 7 8h3.5v8H7l5 5 5-5h-3.5V8H17z'/%3E%3C/svg%3E") center / 24px 24px no-repeat;
  }
`;

/* "Select a team to trade with" / "Select one of your teams", above each column's dropdown */
export const ColLabel = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 700;
  font-size: clamp(17px, 1.06vw, 41px);
  color: #3e464f;
  line-height: 1.2;
`;

export const TeamHeading = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(10px, 0.62vw, 24px);
`;

export const TeamLogo = styled.img`
  width: clamp(32px, 2.0vw, 77px);
  height: clamp(32px, 2.0vw, 77px);
  object-fit: contain;
  flex-shrink: 0;
`;

export const TeamName = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(16px, 1.0vw, 38px);
  color: #012337;
  line-height: 1.2;
`;

export const TeamDropdownWrap = styled.div`
  position: relative;
`;

export const TeamDropdownBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: clamp(8px, 0.5vw, 19px);
  padding: clamp(8px, 0.5vw, 19px) clamp(36px, 2.25vw, 86px) clamp(8px, 0.5vw, 19px) clamp(10px, 0.63vw, 24px);
  border: 1.5px solid #cad2da;
  border-radius: 10px;
  background: #f0f1f3;
  cursor: pointer;
  text-align: left;
  position: relative;

  &:hover { border-color: #004ea3; }
`;

/* Same box as the dropdown, for a side with nothing to choose (the user has one team): no arrow,
   no hover, not clickable. Render with as="div". */
export const TeamStaticBox = styled(TeamDropdownBtn)`
  cursor: default;
  padding-right: clamp(10px, 0.63vw, 24px);

  &:hover { border-color: #cad2da; }
`;

export const TeamDropdownLogo = styled.img`
  width: clamp(26px, 1.62vw, 62px);
  height: clamp(26px, 1.62vw, 62px);
  object-fit: contain;
  flex-shrink: 0;
`;

export const TeamDropdownLabel = styled.span`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 700;
  color: #012337;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DropdownChevron = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #50647b;
  display: flex;
  align-items: center;
`;

export const TeamDropdownList = styled.div`
  /* Positioned by the Popper that wraps it (portaled to the body so the
     scrolling trade column cannot clip it), so no absolute offsets here. */
  width: 100%;
  margin-top: 4px;
  background: #fff;
  border: 1.5px solid #cad2da;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 300;
  max-height: 40vh;
  overflow-y: auto;
`;

export const TeamDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(10px, 0.62vw, 24px);
  padding: clamp(8px, 0.5vw, 19px) clamp(14px, 0.88vw, 34px);
  cursor: pointer;
  background: ${({ $active }) => $active ? '#eff6ff' : '#fff'};

  &:first-child { border-radius: 10px 10px 0 0; }
  &:last-child  { border-radius: 0 0 10px 10px; }
  &:hover { background: #f1f5f9; }

  img {
    width: clamp(24px, 1.5vw, 58px);
    height: clamp(24px, 1.5vw, 58px);
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    font-family: 'Saira Semi Condensed', sans-serif;
    font-size: clamp(13px, 0.81vw, 31px);
    font-weight: 700;
    color: #012337;
  }
`;

export const YearLabel = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 700;
  font-size: clamp(16px, 1.0vw, 38px);
  color: #50647b;
`;

export const PickGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(10px, 0.62vw, 24px);
`;

export const PickTile = styled.div`
  width: clamp(44px, 2.75vw, 106px);
  height: clamp(44px, 2.75vw, 106px);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 700;
  font-size: clamp(15px, 0.94vw, 36px);
  color: ${({ $active }) => $active ? '#004ea3' : '#3e464f'};
  background: ${({ $active }) => $active ? '#fff' : '#f0f1f3'};
  /* Selected: a 1px ring. No colour given, so it takes the tile's text colour (#004ea3). */
  box-shadow: ${({ $active }) => ($active ? '0 0 0 1px' : 'none')};
  cursor: pointer;
  user-select: none;
  transition: color 0.12s, background 0.12s, box-shadow 0.12s;

  /* Hover ring on unselected tiles only; a selected tile keeps its own ring */
  &:hover {
    box-shadow: ${({ $active }) => ($active ? '0 0 0 1px #004ea3' : '0 0 0 1px #aeb0bc')};
  }
`;

export const TradeFooter = styled.div`
  padding: clamp(14px, 0.88vw, 34px) clamp(24px, 1.5vw, 58px);
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: clamp(12px, 0.75vw, 29px);
  flex-shrink: 0;

  ${MOBILE} {
    padding: 12px 16px;
  }
`;

export const TradeBtn = styled.button`
  padding: clamp(5px, 0.31vw, 12px) clamp(44px, 2.75vw, 106px);
  background: ${({ disabled }) => disabled ? '#e5e7eb' : '#012337'};
  color: ${({ disabled }) => disabled ? '#9ca3af' : '#fff'};
  border: none;
  border-radius: 10px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 700;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.15s;

  &:hover:not(:disabled) { background: #004c7d; }

  /* Offer Trade: the footer's only button, full width */
  ${({ $large }) => $large && `
    width: 100%;
    padding: clamp(12px, 0.75vw, 29px) clamp(24px, 1.5vw, 58px);
    font-size: clamp(17px, 1.06vw, 41px);
  `}
`;

export const SecondaryBtn = styled.button`
  padding: clamp(5px, 0.31vw, 12px) clamp(24px, 1.5vw, 58px);
  background: #fff;
  color: #012337;
  border: 1.5px solid #012337;
  border-radius: 10px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #f1f5f9; }
`;
